import { ClockIcon, SparklesIcon } from '@heroicons/react/20/solid';
import { twMerge } from 'tailwind-merge';

import Skeleton from '@/components/UI/Skeleton';
import { ArrowTrendingDownIcon, CurrencyDollarIcon } from '@heroicons/react/16/solid';
import { promoDiscountDollars } from './calculatePrice';

export const PriceInfo: React.FC<{
  price: number;
  originalPrice: number;
  earlyBird?: number;
  earlyBirdEndDate?: string;
  locale?: string;
  variety?: API.MembershipProductOffer;
  stats?: API.MembershipProductStats;
  discount?: number;
  balance?: number;
  /** Spendable promotional credit (#1622). Non-refundable, spent BEFORE
   *  refundable balance, matching the server split (MembershipOrder.splitCredit).
   *  Does not apply to subscriptions. */
  promoCredit?: { total: number; expiresAt?: string | null } | null;
  /** A promotional DISCOUNT grant (#1689) applicable to this product — a one-shot
   *  percentage or fixed amount off, applied to the price BEFORE promo credit /
   *  refundable balance (mirroring the server). Non-refundable; does not apply to
   *  subscriptions or to flat win-back / upgrade prices. */
  promoDiscount?: {
    kind: 'pct' | 'amount';
    pct: number | null;
    amount: number | null;
    expiresAt?: string | null;
  } | null;
  winback?: { price: number; expiresAt?: string | null } | null;
  upgrade?: { price: number; introPaid?: number; eligibleUntil?: string | null } | null;
  loading?: boolean;
}> = ({
  price,
  earlyBird: earlyBirdRaw,
  locale,
  originalPrice,
  variety,
  // stats,
  discount = 0,
  balance = 0,
  promoCredit,
  promoDiscount,
  winback,
  upgrade,
  earlyBirdEndDate,
  loading = false,
}) => {
  // Credit applied at checkout, in the SAME order the server draws it
  // (MembershipOrder.splitCredit): promotional credit first, then refundable
  // balance. Neither applies to subscriptions — the server forces useBalance to
  // false there, so a subscription is never discounted by credit. `price` here is
  // the final offer price (post-discount / flat win-back / flat upgrade), so
  // `netPrice` is exactly what the Stripe intent will charge. The Stripe amount
  // is always recomputed server-side, so this only keeps the shown price honest.
  const isSubscription = !!variety?.time?.subscription?.interval;
  // A promotional discount grant (#1689) applies BEFORE credit/balance, but not on
  // subscriptions and not alongside a flat win-back/upgrade price (mirroring the
  // server, which never stacks it on those). Shared with the option cards + promo
  // banner (see calculatePrice) so every surface nets out the same discount;
  // `variety` carries the subscription / win-back / upgrade flags the helper needs
  // to know when NOT to apply it.
  const discountDollars = promoDiscountDollars(price, variety, promoDiscount);
  const afterDiscount = Math.max(0, price - discountDollars);
  const promoTotal = isSubscription ? 0 : Math.max(0, promoCredit?.total || 0);
  const promoUsed = Math.min(promoTotal, afterDiscount);
  const balanceUsed = isSubscription
    ? 0
    : Math.min(balance, Math.max(0, afterDiscount - promoUsed));
  const creditApplied = promoUsed + balanceUsed;
  const netPrice = Math.max(0, afterDiscount - creditApplied);
  const priceSuffix = isSubscription ? '/ month' : '';
  const isAnnual = variety?.time?.subscription?.interval === '1 years';
  // "expires <date>" note for the promo credit — the last full day the soonest
  // grant is valid, in Pacific business time (the grant expires at the instant
  // after that day). Only shown when promo credit is actually applied.
  const promoExpires =
    promoUsed > 0 && promoCredit?.expiresAt
      ? new Intl.DateTimeFormat(locale || 'en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          timeZone: 'America/Los_Angeles',
        }).format(new Date(new Date(promoCredit.expiresAt).getTime() - 1000))
      : '';
  const fmtUSD0 = (n: number) =>
    new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(n);
  // Promotional discount grant (#1689) label + "expires <date>" note, shown when
  // the discount actually reduces the price. A percentage reads "50% off (−$X)";
  // a fixed amount reads "$X off".
  const promoDiscountLabel =
    discountDollars > 0
      ? promoDiscount?.kind === 'pct'
        ? `${Math.round((promoDiscount.pct || 0) * 100)}% off · ${fmtUSD0(discountDollars)}`
        : `${fmtUSD0(discountDollars)} off`
      : '';
  const promoDiscountExpires =
    discountDollars > 0 && promoDiscount?.expiresAt
      ? new Intl.DateTimeFormat(locale || 'en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          timeZone: 'America/Los_Angeles',
        }).format(new Date(new Date(promoDiscount.expiresAt).getTime() - 1000))
      : '';
  // "Applied" notes shown under the green "Your price", styled exactly like the
  // existing balance line so promo reads as a natural part of the normal price
  // info. Promo is listed first (it is spent first). Reused by the win-back /
  // upgrade / standard blocks so every price path shows the same breakdown.
  const creditNotes =
    creditApplied > 0 ? (
      <>
        {promoUsed > 0 && (
          <div className="mt-2 flex flex-row items-center space-x-1 font-medium text-xs">
            <CurrencyDollarIcon height={16} className="size-4 shrink-0" />
            <span>
              With {fmtUSD0(promoUsed)} of your {fmtUSD0(promoTotal)} promotional credit applied
              {promoExpires && ` · expires ${promoExpires}`}
            </span>
          </div>
        )}
        {balanceUsed > 0 && (
          <div className="mt-2 flex flex-row items-center space-x-1 font-medium text-xs">
            <CurrencyDollarIcon height={16} className="size-4 shrink-0" />
            <span>
              With {fmtUSD0(balanceUsed)} of your {fmtUSD0(balance)} balance applied
            </span>
          </div>
        )}
      </>
    ) : null;

  // The green "Your price" block for the FLAT-price branches (win-back / upgrade),
  // shown only when credit further reduces that flat price. Same treatment as the
  // standard branch's own "Your price" block so all three paths read the same.
  const flatYourPriceBlock =
    creditApplied > 0 ? (
      <div className="mt-4 text-emerald-700 dark:text-emerald-100">
        <p className="flex flex-row space-x-1 font-medium text-sm">
          <ArrowTrendingDownIcon height={16} className="size-4" />
          <span>Your price</span>
        </p>
        <p className="flex flex-row items-baseline space-x-2 font-semibold text-3xl tracking-tight">
          <span>{fmtUSD0(netPrice)}</span>
        </p>
        {creditNotes}
      </div>
    ) : null;

  // Per-user NFL win-back price (#1313): a flat campaign price already reflected
  // in `price`. It renders exactly like the sale badge — a red "Save $X · Ends
  // <date>" pill with the campaign price big and the catalog price struck through
  // — so the early-bird pill and the green "Your price" block are both suppressed.
  const hasWinback = !!winback && typeof winback.price === 'number';
  // CFB intro -> season upgrade (#1491): `price` is already the upgraded amount.
  // Render like the win-back markdown (big upgraded price, struck full season) but
  // in the positive "upgrade" framing. Suppressed when win-back also applies (they
  // are mutually exclusive; win-back wins).
  const hasUpgrade = !hasWinback && !!upgrade && typeof upgrade.price === 'number';
  const introPaid = hasUpgrade ? upgrade?.introPaid || 0 : 0;
  // Full season price = the upgraded price plus the intro credit that was applied.
  const upgradeFullPrice = hasUpgrade ? price + introPaid : 0;
  const earlyBird = hasWinback || hasUpgrade ? undefined : earlyBirdRaw;
  const showYourPrice =
    !hasWinback &&
    !hasUpgrade &&
    (!!discount || !!balanceUsed || !!promoUsed || discountDollars > 0);
  const displayPrice = originalPrice + (variety?.addOn?.price || 0);
  // Anchor the win-back against the full MSRP (catalog price + any early-bird),
  // not the already-discounted current price, so the savings reflect the true
  // markdown (e.g. $750 struck from the $1,200 total, saving $450 — not $250 off
  // the $1,000 sale price). The last full day it is valid (the grant expires at
  // the instant after that day, in Pacific business time) drives the pill date.
  const winbackTotal = displayPrice + (earlyBirdRaw || 0);
  const winbackSave = hasWinback ? Math.max(0, winbackTotal - price) : 0;
  const winbackEnds =
    hasWinback && winback?.expiresAt
      ? new Intl.DateTimeFormat(locale || 'en-US', {
          month: 'short',
          day: 'numeric',
          timeZone: 'America/Los_Angeles',
        }).format(new Date(new Date(winback.expiresAt).getTime() - 1000))
      : '';

  if (hasUpgrade) {
    return (
      <div className="mb-2">
        <div className="mb-3">
          <p className="inline-flex w-fit items-center gap-1.5 rounded-full bg-emerald-500 px-3 py-1">
            <SparklesIcon height={14} className="size-3.5 shrink-0 text-white" />
            <span className="whitespace-nowrap font-semibold text-white text-xs uppercase tracking-wide">
              Upgrade ·{' '}
              {new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
              }).format(introPaid)}{' '}
              intro credit
            </span>
          </p>
        </div>
        <div className="flex items-baseline">
          <p className="font-extrabold text-4xl tracking-tight text-zinc-900 sm:text-5xl dark:text-zinc-100">
            <Skeleton loading={loading}>
              <span>
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  maximumFractionDigits: 0,
                }).format(price)}
              </span>
            </Skeleton>
          </p>
          {upgradeFullPrice > price && (
            <p className="ms-3 font-medium text-xl text-zinc-400 line-through">
              {new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
              }).format(upgradeFullPrice)}
            </p>
          )}
        </div>
        <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
          Upgrade from your Weeks 0-1 intro to the full season.
        </p>
        {flatYourPriceBlock}
      </div>
    );
  }

  if (hasWinback) {
    return (
      <div className="mb-2">
        <div className="mb-3">
          {/* Mobile: compact single-line pill */}
          <p className="inline-flex w-fit items-center gap-1 rounded-full bg-red-500 px-2.5 py-0.5 sm:hidden">
            <ClockIcon height={11} className="size-2.5 shrink-0 text-white" />
            <span className="whitespace-nowrap text-[10px] font-semibold uppercase text-white">
              Save{' '}
              {new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
              }).format(winbackSave)}
              {winbackEnds && ` · Ends ${winbackEnds}`}
            </span>
          </p>
          {/* sm+: single-line pill badge */}
          <p className="hidden w-fit items-center gap-1.5 rounded-full bg-red-500 px-3 py-1 sm:inline-flex">
            <ClockIcon height={14} className="size-3.5 shrink-0 text-white" />
            <span className="whitespace-nowrap font-semibold text-white text-xs uppercase tracking-wide">
              Save{' '}
              {new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
              }).format(winbackSave)}
              {winbackEnds && ` · Ends ${winbackEnds}`}
            </span>
          </p>
        </div>
        <div className="flex items-baseline">
          <p className="font-extrabold text-4xl tracking-tight text-zinc-900 sm:text-5xl dark:text-zinc-100">
            <Skeleton loading={loading}>
              <span>
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  maximumFractionDigits: 0,
                }).format(price)}
              </span>
            </Skeleton>
          </p>
          <p className="ms-3 font-medium text-xl text-zinc-400 line-through">
            {new Intl.NumberFormat(locale, {
              style: 'currency',
              currency: 'USD',
              maximumFractionDigits: 0,
            }).format(winbackTotal)}
          </p>
        </div>
        {flatYourPriceBlock}
      </div>
    );
  }

  return (
    <div className="mb-2">
      {!!earlyBird && (
        <div className="mb-3">
          {/* Mobile: compact single-line pill */}
          <p className="inline-flex w-fit items-center gap-1 rounded-full bg-red-500 px-2.5 py-0.5 sm:hidden">
            <ClockIcon height={11} className="size-2.5 shrink-0 text-white" />
            <span className="whitespace-nowrap text-[10px] font-semibold uppercase text-white">
              Save{' '}
              {new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
              }).format(earlyBird)}
              {earlyBirdEndDate && ` · Ends ${earlyBirdEndDate}`}
            </span>
          </p>
          {/* sm+: single-line pill badge */}
          <p className="hidden w-fit items-center gap-1.5 rounded-full bg-red-500 px-3 py-1 sm:inline-flex">
            <ClockIcon height={14} className="size-3.5 shrink-0 text-white" />
            <span className="whitespace-nowrap font-semibold text-white text-xs uppercase tracking-wide">
              Save{' '}
              {new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
              }).format(earlyBird)}
              {earlyBirdEndDate && ` · Ends ${earlyBirdEndDate}`}
            </span>
          </p>
        </div>
      )}
      {/* Hero price. When a discount/credit reduces the price, the net amount is
          the big bold number and turns green (matching the default big-price
          treatment), with the pre-reduction price struck through beside it — a
          single strikethrough, not one per adjustment. */}
      <div className="flex items-baseline gap-3">
        <p
          className={twMerge(
            'font-extrabold text-4xl tracking-tight text-zinc-900 sm:text-5xl dark:text-zinc-100',
            showYourPrice && 'text-emerald-600 dark:text-emerald-400',
          )}
        >
          <Skeleton loading={loading}>
            <span>
              {new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: isAnnual ? 2 : 0,
              }).format(
                showYourPrice
                  ? isAnnual
                    ? netPrice / 12
                    : netPrice
                  : isAnnual
                    ? displayPrice / 12
                    : displayPrice,
              )}
            </span>
          </Skeleton>
          {priceSuffix && (
            <span className="ml-1 font-normal text-semibold text-sm text-zinc-600 leading-6 tracking-normal dark:text-zinc-400">
              {priceSuffix}
            </span>
          )}
        </p>
        {(showYourPrice || !!earlyBird) && (
          <p className="font-medium text-xl text-zinc-400 line-through dark:text-zinc-500">
            {new Intl.NumberFormat(locale, {
              style: 'currency',
              currency: 'USD',
              maximumFractionDigits: isAnnual ? 2 : 0,
            }).format(
              (() => {
                // When reduced, strike the pre-credit/pre-discount price; otherwise
                // (early-bird only) strike the full catalog price.
                const struck = showYourPrice ? displayPrice : displayPrice + (earlyBird || 0);
                return isAnnual ? struck / 12 : struck;
              })(),
            )}
          </p>
        )}
      </div>
      {showYourPrice && (!!creditNotes || !!discount || !!promoDiscountLabel) && (
        <div className="mt-3 text-emerald-700 dark:text-emerald-100">
          {!!discount && <p className="font-medium text-sm">{discount * 100}% off</p>}
          {!!promoDiscountLabel && (
            <p className="font-medium text-sm">
              {promoDiscountLabel}
              {promoDiscountExpires && (
                <span className="font-normal"> · expires {promoDiscountExpires}</span>
              )}
            </p>
          )}
          {creditNotes}
        </div>
      )}
    </div>
  );
};
